/**
 * PublicNav — shared fixed navigation bar for all public pages.
 *
 * Desktop (> 900px): Logo | center links | LangSwitcher + CurrencyToggle + Login + Register
 * Tablet / Mobile (≤ 900px): Logo | Hamburger → side drawer
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useLang, LangSwitcher, CurrencyToggle } from '../i18n';

// ── Breakpoints ────────────────────────────────────────────────────────────
const BP_TABLET = 900;
const BP_MOBILE = 600;

function useWindowWidth() {
    const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    useEffect(() => {
        const h = () => setW(window.innerWidth);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);
    return w;
}

const NAV_HEIGHT = 65; // px — keep in sync with spacer

const LINK_STYLE        = { color: '#94a3b8', textDecoration: 'none', fontSize: 14, fontWeight: 500 };
const LINK_STYLE_ACTIVE = { ...LINK_STYLE, color: '#60a5fa', fontWeight: 600 };

const BTN_LOGIN = {
    padding: '8px 18px', background: 'none',
    border: '1px solid rgba(29,78,216,0.4)', borderRadius: 10,
    color: '#60a5fa', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const BTN_REGISTER = {
    padding: '8px 18px',
    background: 'linear-gradient(135deg,#1d4ed8,#06b6d4)',
    color: '#fff', border: 'none', borderRadius: 10,
    fontWeight: 700, fontSize: 14, cursor: 'pointer',
};

/**
 * @param {object} style — extra styles merged into the <nav> element
 */
export default function PublicNav({ style }) {
    const navigate   = useNavigate();
    const location   = useLocation();
    const { t }      = useLang();
    const isLoggedIn = !!localStorage.getItem('token');
    const nav        = t('nav') || {};
    const path       = location.pathname;
    const width      = useWindowWidth();
    const [menuOpen, setMenuOpen] = useState(false);

    // Close drawer on route change
    useEffect(() => { setMenuOpen(false); }, [location.pathname]);

    const isSmall  = width <= BP_TABLET; // tablet or mobile — show hamburger
    const isMobile = width <= BP_MOBILE;

    const navPad = isMobile ? '10px 16px' : isSmall ? '10px 24px' : '10px 48px';

    const LINKS = [
        { to: '/',             label: 'Home' },
        { to: '/services',     label: nav.services     || 'Services' },
        { to: '/pricing',      label: nav.pricing      || 'Pricing' },
        { to: '/partner',      label: nav.partner      || 'Partner' },
        { to: '/faq',          label: nav.faq          || 'FAQ' },
        { to: '/terms',        label: nav.terms        || 'Terms' },
        { to: '/api-docs',     label: nav.apiDocs      || 'API' },
    ];

    return (
        <>
            <nav
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: navPad,
                    background: 'rgba(5,8,16,0.95)', backdropFilter: 'blur(20px)',
                    borderBottom: '1px solid rgba(29,78,216,0.15)',
                    fontFamily: '"Segoe UI", system-ui, sans-serif',
                    ...style,
                }}
                className="og-nav"
            >
                {/* Logo */}
                <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#f1f5f9', flexShrink: 0 }}>
                    <img src="/logo.svg" alt="OrbitGrowth" style={{ height: isMobile ? 42 : 56, width: 'auto' }} />
                </Link>

                {/* Center links — desktop only */}
                {!isSmall && (
                    <div
                        style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 24, alignItems: 'center' }}
                        className="og-nav-links"
                    >
                        {LINKS.map(l => (
                            <Link key={l.to} to={l.to} style={path === l.to ? LINK_STYLE_ACTIVE : LINK_STYLE}>{l.label}</Link>
                        ))}
                    </div>
                )}

                {/* Right side */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }} className="og-nav-ctas">
                    {/* Desktop CTAs */}
                    {!isSmall && (
                        <>
                            <LangSwitcher />
                            <CurrencyToggle />
                            <button onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')} style={BTN_LOGIN}>
                                {isLoggedIn ? 'Dashboard' : (nav.login || 'Login')}
                            </button>
                            {!isLoggedIn && (
                                <button onClick={() => navigate('/register')} style={BTN_REGISTER}>
                                    {nav.getStarted || 'Get Started'}
                                </button>
                            )}
                        </>
                    )}

                    {/* Hamburger button (tablet + mobile) */}
                    {isSmall && (
                        <button
                            onClick={() => setMenuOpen(o => !o)}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            style={{
                                background: 'none',
                                border: '1px solid rgba(29,78,216,0.35)',
                                borderRadius: 8, cursor: 'pointer',
                                padding: '8px 10px',
                                display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center',
                            }}
                        >
                            <span style={{
                                display: 'block', width: 22, height: 2,
                                background: menuOpen ? '#60a5fa' : '#94a3b8',
                                borderRadius: 2, transition: 'transform 0.25s, background 0.2s',
                                transform: menuOpen ? 'rotate(45deg) translateY(7px)' : 'none',
                            }} />
                            <span style={{
                                display: 'block', width: 22, height: 2,
                                background: '#94a3b8', borderRadius: 2,
                                transition: 'opacity 0.2s',
                                opacity: menuOpen ? 0 : 1,
                            }} />
                            <span style={{
                                display: 'block', width: 22, height: 2,
                                background: menuOpen ? '#60a5fa' : '#94a3b8',
                                borderRadius: 2, transition: 'transform 0.25s, background 0.2s',
                                transform: menuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
                            }} />
                        </button>
                    )}
                </div>
            </nav>

            {/* Spacer — compensates for fixed nav */}
            <div style={{ height: NAV_HEIGHT }} aria-hidden="true" />

            {/* Drawer + overlay (tablet + mobile) */}
            {isSmall && (
                <>
                    {/* Backdrop overlay */}
                    <div
                        onClick={() => setMenuOpen(false)}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 998,
                            background: 'rgba(0,0,0,0.65)',
                            opacity: menuOpen ? 1 : 0,
                            pointerEvents: menuOpen ? 'auto' : 'none',
                            transition: 'opacity 0.28s',
                        }}
                    />

                    {/* Side drawer — slides in from right */}
                    <div style={{
                        position: 'fixed', top: NAV_HEIGHT, right: 0, bottom: 0,
                        width: isMobile ? 'min(85vw, 300px)' : 320,
                        zIndex: 999,
                        background: 'rgba(6,9,20,0.98)',
                        backdropFilter: 'blur(24px)',
                        borderLeft: '1px solid rgba(29,78,216,0.22)',
                        display: 'flex', flexDirection: 'column',
                        transform: menuOpen ? 'translateX(0)' : 'translateX(100%)',
                        transition: 'transform 0.3s cubic-bezier(0.22,1,0.36,1)',
                        overflowY: 'auto',
                        padding: '16px',
                    }}>
                        {/* Nav links */}
                        {LINKS.map(l => (
                            <Link
                                key={l.to}
                                to={l.to}
                                onClick={() => setMenuOpen(false)}
                                style={{
                                    display: 'block', padding: '12px 14px',
                                    color: path === l.to ? '#60a5fa' : '#94a3b8',
                                    textDecoration: 'none',
                                    fontSize: 15, fontWeight: path === l.to ? 600 : 500,
                                    borderRadius: 10,
                                    background: path === l.to ? 'rgba(29,78,216,0.1)' : 'transparent',
                                    borderLeft: `3px solid ${path === l.to ? '#1d4ed8' : 'transparent'}`,
                                    marginBottom: 2, transition: 'background 0.15s',
                                }}
                            >
                                {l.label}
                            </Link>
                        ))}

                        {/* Divider */}
                        <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '14px 4px' }} />

                        {/* Lang + Currency toggles */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 4px', marginBottom: 14 }}>
                            <LangSwitcher />
                            <CurrencyToggle />
                        </div>

                        {/* Auth buttons */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 4px' }}>
                            <button
                                onClick={() => { navigate(isLoggedIn ? '/dashboard' : '/login'); setMenuOpen(false); }}
                                style={{ ...BTN_LOGIN, width: '100%', padding: '12px 0', borderRadius: 10, textAlign: 'center' }}
                            >
                                {isLoggedIn ? 'Dashboard' : (nav.login || 'Login')}
                            </button>
                            {!isLoggedIn && (
                                <button
                                    onClick={() => { navigate('/register'); setMenuOpen(false); }}
                                    style={{ ...BTN_REGISTER, width: '100%', padding: '12px 0', borderRadius: 10 }}
                                >
                                    {nav.getStarted || 'Get Started'}
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Floating Telegram button — bottom-right, all public pages */}
            <a
                href="https://t.me/orbitgrowth"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Contact us on Telegram"
                style={{
                    position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #229ED9, #1a8cc4)',
                    boxShadow: '0 4px 20px rgba(34,158,217,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(34,158,217,0.7)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.boxShadow = '0 4px 20px rgba(34,158,217,0.55)'; }}
            >
                {/* Official Telegram paper-plane logo */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" fill="white"/>
                </svg>
            </a>
        </>
    );
}
